import { FC } from 'react'

import { useTheme } from '@mui/material'

import { categoryColor } from '../../helpers/diagnosisStyle'
import { MotivationDiagnosisCard, MotivationDiagnosisCategory } from '../../types'
import { DiagnosisColumnShell } from '../DiagnosisPrimitives/DiagnosisColumnShell'
import { DiagnosisCard as DiagnosisCardComponent } from './DiagnosisCard'

interface DiagnosisColumnProps {
  category: MotivationDiagnosisCategory
  cards: MotivationDiagnosisCard[]
  emptyText: string
  /** Game app icon forwarded to each card's gap bar goalpost marker. */
  gameIcon?: string
}

/**
 * Renders the stack of {@link DiagnosisCard}s for a bucket (or a dashed
 * empty-state Paper when the bucket is empty). The category accent comes from
 * {@link categoryColor} so the cards share one source of truth for "what colour
 * means aligned vs blind spot".
 */
export const DiagnosisColumn: FC<DiagnosisColumnProps> = ({ category, cards, emptyText, gameIcon }) => {
  const theme = useTheme()
  const accent = categoryColor(theme, category)

  return (
    <DiagnosisColumnShell
      cards={cards}
      emptyText={emptyText}
      renderCard={(card, index) => (
        // The cards are pre-sorted by signal strength in `buildMotivationDiagnosis`,
        // so `index === 0` reliably identifies the top card per column. Surface a
        // "biggest gap" badge on the top blind spot to give the user one clear
        // priority instead of a flat list of equally-weighted cards.
        <DiagnosisCardComponent
          key={card.motivation.id}
          card={card}
          accent={accent}
          isTopOfBucket={index === 0 && card.category === 'blind_spot'}
          gameIcon={gameIcon}
        />
      )}
    />
  )
}
